import React, { useState } from 'react';
import { Box, Button, Pagination, TextField } from '@mui/material';
import type { PaginationProps } from '@mui/material';

export interface CatalogPaginationProps {
  count: number;
  page: number; // 1-based
  onChange: (event: React.ChangeEvent<unknown>, page: number) => void;
  color?: PaginationProps['color'];
  size?: PaginationProps['size'];
}

export function CatalogPagination({
  count,
  page,
  onChange,
  color = 'primary',
  size,
}: CatalogPaginationProps) {
  const [jumpValue, setJumpValue] = useState('');

  const handleJump = (event: React.SyntheticEvent) => {
    const parsed = parseInt(jumpValue, 10);
    if (isNaN(parsed)) {
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), count);
    const syntheticEvent = {} as React.ChangeEvent<unknown>;
    onChange(syntheticEvent, clamped);
    setJumpValue('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleJump(event);
    }
  };

  return (
    <Box
      data-testid="catalog-pagination"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      <Pagination
        count={count}
        page={page}
        onChange={onChange}
        color={color}
        size={size}
        siblingCount={1}
        boundaryCount={2}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          inputProps={{
            'data-testid': 'pagination-jump-input',
            min: 1,
            max: count,
            'aria-label': 'Go to page',
          }}
          placeholder="Go to page"
          size="small"
          type="number"
          value={jumpValue}
          onChange={(e) => setJumpValue(e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{ width: size === 'small' ? 90 : 100 }}
        />
        <Button
          data-testid="pagination-jump-go"
          variant="outlined"
          size="small"
          onClick={handleJump}
          sx={{ minWidth: 'unset', px: 1 }}
        >
          Go
        </Button>
      </Box>
    </Box>
  );
}
